import {useNavigate} from 'react-router-dom';
import Button from '../../../../../components/button';
import DotsLoading from '../../../../../components/dotsLoading';
import SpinnerLoading from '../../../../../components/SpinnerLoading';
import useGetBankValidationData from '../../../../../services/hookService/getBankValidationData/useGetBankValidationData';
import usePatchDirectLoanRequest from '../../../../../services/hookService/patchDirectLoanRequest/usePatchDirectLoanRequest';
import usePatchLoanRequest from '../../../../../services/hookService/patchLoanRequest/usePatchLoanRequest';
import ValidationLetter from './ValidationLetter';
import ValidationScoreBoard from './ValidationScoreBoard';
import usePostLoanValidationRequest from '../../../../../services/hookService/postLoanValidationRequest/usePostLoanValidationRequest';
import UpgradeAccBtmSheet from '../status1/UpgradeAccBtmSheet';
import {useState} from 'react';
import {AxiosError} from 'axios';
import useGetBankValidationReport from '../../../../../services/hookService/getBankValidationReport';
import {ErrorCode} from '../../../../../global/helpers/ErrorCodes/ErrorCode';

const BankValidationResult = ({loanItem}: {loanItem: ILoanRequestItem}) => {
  const navigate = useNavigate();
  const [isOpenUpgradeAcc, setIsOpenUpgradeAcc] = useState<boolean>(false);

  const {data, isLoading} = useGetBankValidationData({
    enabled: true,
    loanGuid: loanItem?.guid,
  });

  const {mutate: mutatePatchLoan, isLoading: isLoadingPatchLoan} =
    usePatchLoanRequest();

  const {mutate: mutatePatchDirectLoan, isLoading: isLoadingPatchDirectLoan} =
    usePatchDirectLoanRequest();

  const {
    mutate: mutateLoanValidation,
    isLoading: isMutateLoanValidationLoading,
  } = usePostLoanValidationRequest();

  const {data: reportData, refetch: refetchBankValidationReport} =
    useGetBankValidationReport(loanItem.guid);
  const handleRevalidationRequest = () => {
    const payload: IPatchLoanRequestPayload | IPatchDirectLoanRequestPayload = {
      loan_request_guid: loanItem.guid,
      status: 1,
    };

    const onSuccess = () => navigate('/my-credits/startLoanCredit');

    loanItem.is_direct
      ? mutatePatchDirectLoan(payload, {onSuccess})
      : mutatePatchLoan(payload, {onSuccess});
  };

  const handleOpenPdf = () => {
    if (reportData?.report_file) {
      // If it's a File object, create a URL from it
      const fileUrl =
        reportData.report_file instanceof File
          ? URL.createObjectURL(reportData.report_file)
          : reportData.report_file; // If it's already a string

      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = 'bank-validation-report.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the object URL if it was created
      if (reportData.report_file instanceof File) {
        URL.revokeObjectURL(fileUrl);
      }
    }
  };

  const handleSeeDetails = () => {
    mutateLoanValidation(loanItem.guid, {
      onSuccess: async () => {
        await refetchBankValidationReport();
        handleOpenPdf();
      },
      onError: async error => {
        const axiosError = error as AxiosError<{message: string[]}>;
        const errorCode = axiosError.response?.status;

        if (
          errorCode === 400 &&
          // @ts-ignore
          axiosError?.response?.data.error_code ===
            ErrorCode.PAID_VALIDATION_REQUEST
        ) {
          await refetchBankValidationReport();
          handleOpenPdf();
        }
        if (errorCode === 402) {
          setIsOpenUpgradeAcc(true);
        }
      },
    });
  };

  const isLoadingRevalidation = isLoadingPatchLoan || isLoadingPatchDirectLoan;

  if (!data?.score) return null;

  return (
    <div>
      <div className="space-y-2">
        <span className="text-gray-800 font-Medium text-md">
          اعتبارسنجی بانکی
        </span>
        <div className="flex flex-col items-center justify-center gap-2 p-3 rounded-lg bg-gray-50">
          {isLoading && <SpinnerLoading />}

          {!isLoading && data && (
            <>
              <div className="flex flex-col items-center justify-center gap-3">
                <ValidationScoreBoard score={+data.score} />
                <ValidationLetter letter={data.risk} />
              </div>

              <span className="mt-1 text-sm font-medium text-gray-900">
                پیش‌بینی میزان خوش حسابی (بازپرداخت مناسب)
              </span>

              <div className="flex items-center justify-between w-full mt-3 text-md">
                <span className="text-gray-700">رتبه اعتباری</span>
                <span className="font-semibold ">{data.risk}</span>
              </div>

              <div className="flex items-center justify-between w-full mt-3 text-md">
                <span className="text-gray-700">وضعیت اعتبارسنجی</span>
                <span className="font-medium ">تایید شده</span>
              </div>

              <div className="w-full h-[1px] bg-gray-300 my-2" />

              <span className="self-start text-gray-700 justify-self-start text-md">
                اعتبارسنجی تا 1 ماه معتبر می‌باشد
              </span>

              <div className="flex items-center w-full gap-2 mt-2">
                <Button
                  className="w-full px-3 py-2 rounded-lg text-farm-shop-500 border-[1px] border-farm-shop-500 text-md"
                  variant="outline"
                  onClick={handleRevalidationRequest}
                  disabled={isLoadingRevalidation}
                >
                  {isLoadingRevalidation ? <DotsLoading /> : 'اعتبارسنجی مجدد'}
                </Button>
                <Button
                  className="w-full px-3 py-2 text-white rounded-lg bg-farm-shop-500 text-md"
                  onClick={handleSeeDetails}
                  isLoading={isMutateLoanValidationLoading}
                >
                  مشاهده جزئیات
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
      <UpgradeAccBtmSheet
        isOpen={isOpenUpgradeAcc}
        setIsOpen={setIsOpenUpgradeAcc}
        loanGuid={loanItem?.guid!}
      />
    </div>
  );
};

export default BankValidationResult;
